import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaBullseye, FaEye, FaHeart } from 'react-icons/fa';

const AboutUs = () => {
  const stats = [
    { value: '10,000+', label: 'Active Buyers' },
    { value: '500+', label: 'Verified Sellers' },
    { value: '50,000+', label: 'Products Listed' },
    { value: '7', label: 'Emirates Served' },
  ];

  const values = [
    {
      icon: <FaBullseye className="w-7 h-7" />,
      title: 'Our Mission',
      desc: 'To make quality products accessible to everyone in the UAE through a fast, reliable, and trustworthy online marketplace.',
    },
    {
      icon: <FaEye className="w-7 h-7" />,
      title: 'Our Vision',
      desc: "To become the region's most trusted marketplace, connecting local sellers with customers across the Gulf and beyond.",
    },
    {
      icon: <FaHeart className="w-7 h-7" />,
      title: 'Our Values',
      desc: 'Transparency, quality, and customer-first service guide every decision we make, from sourcing to delivery.',
    },
  ];

  return (
    <div className="bg-[#f5f5f5]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white text-gray-900 border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#E21C1B]/6 blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E21C1B]/4 blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto relative z-10 py-20 md:py-14 sm:py-12 text-center">
          <span className="inline-block bg-[#E21C1B]/10 border border-[#E21C1B]/20 text-[#E21C1B] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            About Pikko Dubai
          </span>
          <h1 className="text-5xl md:text-4xl sm:text-3xl font-extrabold mb-5 leading-tight text-gray-900">
            Your Trusted Marketplace<br />
            <span className="text-[#E21C1B]">Across the UAE</span>
          </h1>
          <p className="text-gray-500 text-lg sm:text-base max-w-2xl mx-auto mb-10 leading-relaxed">
            Pikko Dubai connects thousands of buyers with verified sellers across the Emirates,
            delivering genuine products, fast shipping, and a shopping experience you can rely on.
          </p>

          <div className="flex items-center justify-center gap-8 sm:gap-5 flex-wrap">
            {stats.map((stat, i, arr) => (
              <React.Fragment key={stat.label}>
                <div>
                  <div className="text-2xl font-extrabold text-gray-900">{stat.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                </div>
                {i < arr.length - 1 && <div className="w-px h-8 bg-gray-200 flex-shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-12 items-center">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 order-2 md:order-1">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Built for the UAE, by people who know it</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                We started Pikko Dubai with a simple goal: give shoppers across the Emirates a
                marketplace they can trust, and give local sellers a real chance to grow.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Every seller on our platform is verified, every product is checked for quality,
                and every order is backed by our support team — from checkout to delivery.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl sm:text-2xl font-bold text-gray-900 mb-4">Our Story</h2>
              <p className="text-gray-500 leading-relaxed mb-4">
                What began as a small marketplace for local sellers has grown into one of the
                UAE's go-to destinations for quality products at fair prices. We work directly
                with trusted brands and independent sellers to bring a wide range of goods to
                your doorstep — quickly and reliably.
              </p>
              <p className="text-gray-500 leading-relaxed">
                Today, we serve customers across all seven Emirates, with a growing catalog and
                a seller community that shares our commitment to quality and honesty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-16 bg-white">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-2xl font-bold text-gray-900 mb-3">What Drives Us</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              The principles that shape every product we list and every order we deliver.
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-1 sm:grid-cols-1 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-[#f9f9f9] rounded-xl p-8 border border-gray-100 text-center hover:shadow-md transition-all duration-200"
              >
                <div className="w-16 h-16 mx-auto bg-[#E21C1B]/10 text-[#E21C1B] rounded-xl flex items-center justify-center mb-5">
                  {v.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="py-16 bg-[#1a1a1a] text-white">
        <div className="w-[85%] md:w-[90%] sm:w-[95%] mx-auto text-center">
          <h2 className="text-3xl sm:text-2xl font-bold mb-4">Ready to Start Shopping?</h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            Explore thousands of products from verified sellers across the UAE, with fast delivery
            right to your door.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/products"
              className="inline-block px-10 py-4 bg-[#E21C1B] text-white rounded-md font-bold text-base hover:bg-[#c01717] transition-all duration-200"
            >
              Shop Now
            </Link>
            <Link
              to="/become-seller"
              className="inline-block px-10 py-4 border border-white/20 text-white rounded-md font-bold text-base hover:border-white/40 transition-all duration-200"
            >
              Become a Seller
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
